import { formatCurrency } from "@/lib/currency";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle } from "lucide-react";
import { LoadingButton } from "@/components/ui/loading-button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const CANCEL_REASONS = [
  "Customer requested cancellation",
  "Vehicle unavailable",
  "Scheduling conflict",
  "Driver unavailable",
  "Weather conditions",
  "Duplicate job",
  "Other",
];

interface CancelJobModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  vehicleReg: string;
  defaultAbortFee: number;
  onCancelled: () => void;
}

export function CancelJobModal({
  open,
  onOpenChange,
  jobId,
  vehicleReg,
  defaultAbortFee,
  onCancelled,
}: CancelJobModalProps) {
  const [abortFee, setAbortFee] = useState<string>(defaultAbortFee.toFixed(2));
  const [reasonType, setReasonType] = useState<string>("");
  const [customReason, setCustomReason] = useState("");
  const [sendInvoice, setSendInvoice] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { toast } = useToast();

  const reason = reasonType === "Other" ? customReason : reasonType;
  const feeValue = parseFloat(abortFee);
  const isValid = !isNaN(feeValue) && feeValue >= 0 && reason.trim().length > 0;

  const handleSubmit = () => {
    if (!isValid) return;
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setShowConfirm(false);
    setSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke("cancel-job", {
        body: {
          jobId,
          abortFee: feeValue,
          reason: reason.trim(),
          sendInvoice,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.warning) {
        toast({
          title: "Job cancelled — invoice generated but not sent",
          description: `${data.warning} The job was cancelled successfully. Add the customer's email and send the invoice manually.`,
          variant: "destructive",
          duration: 15000,
        });
      } else {
        toast({
          title: data?.invoiceNumber ? "Job cancelled — invoice generated" : "Job cancelled",
          description: data?.message || "Job has been marked as completed (aborted).",
        });
      }

      onOpenChange(false);
      onCancelled();
    } catch (err: any) {
      console.error("Cancel job error:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to cancel job",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (val: boolean) => {
    if (!submitting) {
      onOpenChange(val);
      if (!val) {
        // Reset state
        setAbortFee(defaultAbortFee.toFixed(2));
        setReasonType("");
        setCustomReason("");
        setSendInvoice(true);
      }
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Cancel Job
            </DialogTitle>
            <DialogDescription>
              Cancelling will mark this job as completed (aborted) and generate
              an abort invoice for <strong>{vehicleReg}</strong>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Abort Fee */}
            <div className="space-y-2">
              <Label htmlFor="abort-fee">Abort Fee (£)</Label>
              <Input
                id="abort-fee"
                type="number"
                min="0"
                step="0.01"
                value={abortFee}
                onChange={(e) => setAbortFee(e.target.value)}
                placeholder="0.00"
              />
              {abortFee !== "" && (isNaN(feeValue) || feeValue < 0) && (
                <p className="text-xs text-destructive">Fee must be £0.00 or more</p>
              )}
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <Label>Cancellation Reason</Label>
              <Select value={reasonType} onValueChange={setReasonType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason..." />
                </SelectTrigger>
                <SelectContent>
                  {CANCEL_REASONS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {reasonType === "Other" && (
                <Textarea
                  placeholder="Enter cancellation reason..."
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  rows={2}
                />
              )}
            </div>

            {/* Send Invoice toggle */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="send-invoice"
                checked={sendInvoice}
                onChange={(e) => setSendInvoice(e.target.checked)}
                className="rounded border-input"
              />
              <Label htmlFor="send-invoice" className="text-sm font-normal cursor-pointer">
                Send invoice now (default ON)
              </Label>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={submitting}
            >
              Close
            </Button>
            <LoadingButton
              variant="destructive"
              onClick={handleSubmit}
              disabled={!isValid}
              loading={submitting}
              loadingText="Cancelling…"
            >
              Cancel Job & Invoice
            </LoadingButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Final confirmation */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Cancellation</AlertDialogTitle>
            <AlertDialogDescription>
              {feeValue > 0
                ? `This will invoice the customer ${formatCurrency(feeValue.toFixed(2))}. Proceed?`
                : "This will cancel the job with no abort fee. Proceed?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Go Back</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Confirm Cancellation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
